import { type FormEvent, useEffect, useMemo, useState } from "react"
import { Cpu, Plus, RefreshCw, Router, Search } from "lucide-react"
import { smartoltService, type SmartOLTOnuTypes } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { filterRows, formatUpdated } from "./smartoltSettingsUtils.ts"

const empty: SmartOLTOnuTypes = { items: [], summary: { total: 0, total_onus: 0 }, updated_at: null }

const inputCls = "mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 font-normal placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
const labelCls = "text-xs font-medium text-slate-700"

export function SmartOLTOnuTypesPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTOnuTypes>(empty)
  const [search, setSearch] = useState("")
  const [showAdd, setShowAdd] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: "",
    pon_type: "gpon",
    ethernet_ports_nr: "",
    wifi_ssids_nr: "",
    voip_ports_nr: "",
    catv: "0",
    allow_custom_profiles: "0",
    capability: "",
  })

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try { await smartoltService.runSyncNow(25) } catch { /* list cached data */ }
      }
      const next = await smartoltService.getOnuTypes()
      setData({ items: next?.items || [], summary: next?.summary || empty.summary, updated_at: next?.updated_at || null })
    } catch {
      toast.error("Failed to load ONU types")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(
    () => filterRows(data.items, search, ["name", "smartolt_type_id", "pon_type", "capabilities"]),
    [data.items, search],
  )

  const updateForm = (field: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const addOnuType = async (event: FormEvent) => {
    event.preventDefault()
    if (!form.name.trim() || !form.pon_type.trim()) {
      toast.error("Model name and PON type are required")
      return
    }
    const form_data = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value),
    )
    setSaving(true)
    try {
      await smartoltService.createCommand("add_onu_type", { form_data })
      await smartoltService.runCommandsNow(5)
      toast.success("ONU type add command sent")
      setForm({ name: "", pon_type: "gpon", ethernet_ports_nr: "", wifi_ssids_nr: "", voip_ports_nr: "", catv: "0", allow_custom_profiles: "0", capability: "" })
      setShowAdd(false)
      await load(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to add ONU type")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">ONU Types</h1>
          <p className="mt-0.5 text-xs text-slate-500">ONT/ONU device models and their capabilities</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700">
            {data.summary.total} types
          </span>
          <span className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700">
            {data.summary.total_onus} ONUs
          </span>
          <button type="button" onClick={() => setShowAdd((v) => !v)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600">
            <Plus className="h-3.5 w-3.5" />
            Add ONU Type
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <form onSubmit={addOnuType} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs">
          <p className="mb-4 text-sm font-semibold text-slate-800">Add New ONU Type</p>
          <div className="grid gap-3 sm:grid-cols-4">
            <label className={labelCls}>Model name *<input value={form.name} onChange={(e) => updateForm("name", e.target.value)} className={inputCls} /></label>
            <label className={labelCls}>
              PON type *
              <select value={form.pon_type} onChange={(e) => updateForm("pon_type", e.target.value)} className={inputCls}>
                <option value="gpon">GPON</option>
                <option value="epon">EPON</option>
              </select>
            </label>
            <label className={labelCls}>Ethernet ports<input value={form.ethernet_ports_nr} onChange={(e) => updateForm("ethernet_ports_nr", e.target.value)} className={inputCls} type="number" /></label>
            <label className={labelCls}>Wi-Fi SSIDs<input value={form.wifi_ssids_nr} onChange={(e) => updateForm("wifi_ssids_nr", e.target.value)} className={inputCls} type="number" /></label>
            <label className={labelCls}>VoIP ports<input value={form.voip_ports_nr} onChange={(e) => updateForm("voip_ports_nr", e.target.value)} className={inputCls} type="number" /></label>
            <label className={labelCls}>
              CATV
              <select value={form.catv} onChange={(e) => updateForm("catv", e.target.value)} className={inputCls}>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </label>
            <label className={labelCls}>
              Custom profiles
              <select value={form.allow_custom_profiles} onChange={(e) => updateForm("allow_custom_profiles", e.target.value)} className={inputCls}>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </label>
            <label className={labelCls}>Capability<input value={form.capability} onChange={(e) => updateForm("capability", e.target.value)} placeholder="routing, bridge…" className={inputCls} /></label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-xs hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="h-9 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
              {saving ? "Adding…" : "Add ONU Type"}
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/50 p-3">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search models…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20" />
          </div>
          <button type="button" disabled={refreshing} onClick={() => load(true, true)}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Sync from SmartOLT
          </button>
        </div>

        {loading ? (
          <div className="px-4 py-12 text-center">
            <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
            <p className="text-slate-500 text-sm">Loading ONU types…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <Cpu className="mx-auto h-12 w-12 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-800">No ONU types synced yet</p>
            <p className="mt-1 text-xs text-slate-500">Types appear after SmartOLT sync runs.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  {["Model", "PON", "Capabilities", "ONUs", "Synced"].map((h, i) => (
                    <th key={h} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => (
                  <tr key={row.smartolt_type_id} className="transition-colors hover:bg-slate-50">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                          <Router className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <span className="font-medium text-slate-800">{row.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase ${
                        row.pon_type === "gpon"
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : "bg-purple-50 text-purple-700 border border-purple-200"
                      }`}>
                        {row.pon_type}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{row.capabilities || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-700">{row.onus}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{formatUpdated(row.fetched_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
